import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatInteger } from '@/lib/language';
import { buildBreakdown, type ComponentKey } from '@/lib/scoreBreakdown';
import { timeAgo } from '@/lib/timeAgo';
import type { StaleReason } from '@/lib/snapshotFreshness';
import { HeroDonut } from './HeroDonut';

/**
 * Die Score-Karte des Feed-Checks (Entlastung Paket 3, Spec Abschnitt 5.3).
 *
 * Sie trug bis zum 09.09.2026 den halben Bildschirm: Ring, Notenskala,
 * Statuswort, Verdikt, Bezugsgroesse, Delta, Micro-Goal samt Fortschrittsbalken,
 * Methodik-Link, Verlustzeile mit Freigabe-Pill, die drei teuersten Baustellen,
 * einen gefuellten Inline-CTA, den Potenzial-Strip, die sechs Komponenten-Balken
 * und die technischen Checks. Vier von diesen Bloecken zeigten dieselbe Zahl
 * (Audit: "Fassungen des Scores 4"), drei waren Listen mit Baustellen.
 *
 * Uebrig bleibt genau das, was die Frage "wie steht mein Feed da" beantwortet:
 * die Zahl mit der Note IM Ring, ein Satz Bewertung, ein Satz zum groessten
 * Verlust, eine Meta-Zeile. Die Baustellen liegen jetzt in
 * `FeedCheckCostList`, die Handlung in `FeedCheckNextStep`, die Bezugsgroesse
 * und das Pruefdatum in der Seitenkopfzeile.
 *
 * Was nur im DRUCK-Report bleibt (`hidden print:block`): die Notenskala A bis F,
 * die Bezugsgroesse mit Pruefdatum, der Micro-Goal-Satz, der Methodik-Link, der
 * Potenzial-Strip und der Kapitel-Zaehler. Der Report ist ein Dokument, das
 * jemand ohne die App liest; dort tragen diese Bloecke, was am Bildschirm
 * Wiederholung war.
 */

/**
 * Minimal structural shape of a `feed_health_snapshots` row — only the fields
 * this component reads. Intentionally NOT the DB row type (see SCHEMA.md
 * conventions): keeps this component decoupled from Supabase-generated types.
 */
export interface FeedCheckSnapshot {
  avg_score: number;
  product_count: number;
  critical_count: number;
  warning_count: number;
  avg_title_score: number;
  avg_description_score: number;
  avg_highlights_score: number;
  avg_product_details_score: number | null;
  avg_gpc_score: number | null;
  avg_attribute_score: number | null;
  /**
   * Score v3. Dieser Kopf liest die beiden Felder nicht selbst, reicht den
   * Befund aber an `buildBreakdown` weiter -- und DORT entscheiden sie, ob die
   * Attribut-Zeile als groesster Verlust genannt wird oder als "fuer diese
   * Kategorie nicht bewertet".
   *
   * Optional, weil ein Altbefund sie als `null` traegt. Ohne die beiden
   * Zeilen im Typ waeren sie zur Laufzeit trotzdem da (die Seite selektiert
   * `*`) und der Satz zum groessten Verlust haenge an einem Feld, das die
   * Schnittstelle verschweigt.
   *
   * `| string` ist kein Schmuck: `avg_base_score` ist `numeric`, und PostgREST
   * liefert numeric als STRING. `buildBreakdown` rechnet damit (`toNumberOrNull`),
   * dieser Kopf liest die Felder gar nicht. Die sechs `avg_*`-Nachbarn oben
   * tragen denselben Vorbehalt und sind trotzdem als `number | null` deklariert
   * -- Altbestand, den diese Aufgabe nicht anfasst; sichtbar wird er nur daran,
   * dass der Kopf `Math.round(Number(snapshot.avg_score))` schreibt statt die
   * Zahl direkt zu nehmen. Die beiden neuen Felder stimmen lieber, als zur
   * Ungenauigkeit ihrer Nachbarn zu passen.
   */
  attribute_applicable_count?: number | string | null;
  avg_base_score?: number | string | null;
  /**
   * `feed_health_snapshots.snapshot_type` / `.scoring_version`, fuer den
   * Vergleich mit `compareSnapshot`, Spec 2026-09-15. Optional aus demselben
   * Grund wie die beiden Felder oben: ein Altbefund traegt sie nicht, und ein
   * einzelner Snapshot ohne Vergleich braucht sie nicht.
   */
  snapshot_type?: string | null;
  scoring_version?: string | null;
}

export type FeedCheckGrade = 'A' | 'B' | 'C' | 'D' | 'F';

/**
 * Alias auf `ComponentKey`, nicht eine zweite Aufzaehlung derselben sechs
 * Namen: der Schluessel kommt aus `buildBreakdown` und geht durch diesen Kopf
 * nur hindurch. Zwei getrennt gepflegte Unions waeren eine stille Drift-Quelle.
 */
export type FeedCheckIssueKey = ComponentKey;

/**
 * Ab wie vielen Punkten Verlust ein Bereich "spuerbar" ist.
 *
 * Dieselbe 5, die `lossToneClass` in `score-colors.ts` als Warn-Schwelle des
 * Verlust-Balkens benutzt (Spec 5.3: "Warn ab 5, Fehler ab 10"). Eine zweite,
 * unabhaengige Schwelle fuer denselben Begriff waere die Stelle, an der Satz
 * und Balken irgendwann Verschiedenes behaupten: der Satz nennt Attribute als
 * einzigen spuerbaren Verlust, der Balken darunter faerbt drei Zeilen warn.
 */
export const SENSIBLE_LOSS_POINTS = 5;

export interface FeedCheckHeaderProps {
  snapshot: FeedCheckSnapshot;
  /**
   * Slot fuer die Titel-Diffs (`FeedCheckInsights`). Am Bildschirm rendert der
   * Aufrufer hier seit Paket 3 nichts mehr, im Druck-Report schon.
   */
  children?: React.ReactNode;
  /**
   * Es existiert ein Post-Optimierungs-Snapshot fuer diesen Feed.
   *
   * Steuert allein noch den Potenzial-Strip im Druck-Report: der vergleicht
   * den Feed mit dem Median optimierter Feeds, also mit sich selbst, sobald er
   * schon optimiert ist, und behauptete Potenzial, das dieser Feed gerade
   * gehoben hat.
   */
  hasPost?: boolean;
  /**
   * Gemessener Vorher-Score desselben Feeds. "Vorher" ist der eingefrorene
   * ERSTBEFUND (aeltester Befund mit bekannter Formel-Version, Spec 7).
   *
   * Der Aufrufer entscheidet, ob eine Basis taugt -- insbesondere, dass sie
   * nicht der heutige Befund selbst ist. Diese Komponente rechnet, was sie
   * bekommt. Ohne echte Messung bleibt der Wert `null` und die Meta-Zeile sagt
   * ehrlich "noch kein Vergleichswert" statt eine Zahl zu erfinden.
   */
  compareScore?: number | null;
  /**
   * Gemessene Grundgesamtheit der VORHER-Messung (`product_count` derselben
   * Snapshot-Zeile, aus der `compareScore` stammt).
   *
   * Nur noch im Druck-Report genannt: am Bildschirm hat die Meta-Zeile ein
   * Budget von zwoelf Woertern (Spec 4.3), und die Bezugsgroesse eines
   * Vergleichs ist die Angabe, die ein Leser des Reports braucht, nicht die,
   * die eine Handlung ausloest. `null` = nicht gemessen.
   */
  compareCount?: number | null;
  /**
   * Es EXISTIEREN aeltere Befunde, aber keiner taugt als Vergleichsbasis, weil
   * die Formel-Version fehlt oder abweicht (`pickBaseline` filtert sie aus).
   *
   * Ohne dieses Flag sagte die Karte in dem Fall "noch kein Vergleichswert" --
   * direkt nachdem der Nutzer im Erfolgs-Modal ein grosses Plus gesehen hat,
   * liest sich das wie ein Widerspruch. Greift NUR, wenn ohnehin kein Delta
   * gerechnet werden kann (`compareScore` null). Default false.
   */
  baselineReset?: boolean;
  /**
   * Gemessene Zahl ALLER Produkte des Feeds (`useProductStats.total`).
   *
   * Liegt der Feed-Bestand ueber der gescorten Menge, benennt die Meta-Zeile
   * die Luecke -- aber erst ab einem Anteil von einem Prozent (Spec 5.3).
   * Darunter ist die Aussage "1 von 3.777 Produkten hat noch keinen Score"
   * wahr und trotzdem Rauschen: sie kostet ein Viertel des Wortbudgets der
   * Zeile fuer einen Zustand, den der naechste Score-Lauf ohnehin raeumt.
   *
   * `null` oder kleiner/gleich der gescorten Menge = kein Zusatz. Vor allem
   * der Ladezustand faellt darunter (`useProductStats` liefert bis zur
   * Aufloesung 0), und eine erfundene Luecke waere schlimmer als keine.
   */
  feedTotal?: number | null;
  /**
   * Zeitpunkt der Messung (`feed_health_snapshots.created_at`).
   *
   * Am Bildschirm traegt ihn seit Paket 3 die Seitenkopfzeile
   * ("<Feed> · <N> Produkte geprueft vor <Zeit>"); hier bleibt er fuer den
   * Druck-Report, der die Kopfzeile nicht hat. `null` = kein Zeitstempel.
   */
  checkedAt?: string | null;
  /**
   * Warum der Befund nicht mehr traegt (`isSnapshotStale`), oder `null`.
   *
   * `'age'` = aelter als `STALE_AFTER_DAYS`. `'sync'` = seit der Messung wurde
   * synchronisiert, der Befund beschreibt also eine Produktmenge, die es so
   * nicht mehr gibt. Der zweite Grund ist der konkretere und gewinnt.
   *
   * Der Chip bleibt am Bildschirm: er ist die einzige Angabe der Karte, die
   * ihre eigene Zahl in Frage stellt.
   */
  staleReason?: StaleReason | null;
  /** Kapitel-Chip "Kapitel 1 von N" -- nur noch im Druck-Report. Ohne Wert kein Chip. */
  chapterTotal?: 2 | 3 | 4;
  /**
   * Der Erstbefund (pre) zum Vergleich, Spec 2026-09-15 Abschnitt 2.3. Nur der
   * Zusatz am Verlust-Satz haengt daran; ob beide vergleichbar sind (post gegen
   * pre, gleiche Formel-Version), entscheidet buildBreakdown, nicht dieser Kopf.
   */
  compareSnapshot?: FeedCheckSnapshot | null;
  /**
   * WP5 A2O-22: startet eine neue Pruefung. Nur zusammen mit `staleReason`
   * sichtbar; das Badge stellt die eigene Zahl in Frage und muss den Weg zur
   * frischen Zahl anbieten.
   */
  onRecheck?: () => void;
  recheckBusy?: boolean;
}

/**
 * Design-Audit N1: das Delta stand hart in Erfolgsgruen, auch wenn es negativ
 * war. Der Bericht fordert sichtbare negative Deltas ausdruecklich als
 * Gegenmuster zum Hype-Wrapper, der nur Aufwaertspfeile zeigt -- ein Rueckgang
 * in der Erfolgsfarbe ist die getarnte Variante genau dieses Musters: die Zahl
 * ist ehrlich, die Kodierung luegt. Muster: `liftColor` in
 * PerformanceSigKpiCards.
 *
 * Null ist kein Erfolg und kein Fehlschlag, sondern eine Nullaussage, und
 * traegt deshalb den neutralen Ton.
 */
export function scoreDeltaInkClass(delta: number): string {
  if (delta > 0) return 'text-success-ink';
  if (delta < 0) return 'text-destructive-ink';
  return 'text-muted-foreground';
}

/** A >=85, B >=70, C >=55, D >=40, F <40 — on `avg_score`. */
export function gradeForScore(score: number): FeedCheckGrade {
  if (score >= 85) return 'A';
  if (score >= 70) return 'B';
  if (score >= 55) return 'C';
  if (score >= 40) return 'D';
  return 'F';
}

/** Band order for the A-F ladder (Nutri-Score format, research Q2) — Druck-Report. */
export const GRADE_ORDER: FeedCheckGrade[] = ['A', 'B', 'C', 'D', 'F'];

const GRADE_LOWER_BOUND: Record<FeedCheckGrade, number> = { A: 85, B: 70, C: 55, D: 40, F: 0 };

export interface NextGradeGoal {
  targetGrade: FeedCheckGrade;
  pointsNeeded: number;
  /** 0-100 progress within the current band toward the next threshold. */
  progressPct: number;
}

/**
 * Micro goal (research Q3): distance to the next grade threshold.
 * Returns null for grade A. `pointsNeeded` is ceiled and floored at 1 so the
 * copy never claims "0 points to go" while still showing the lower grade.
 */
export function nextGradeGoal(score: number): NextGradeGoal | null {
  const grade = gradeForScore(score);
  if (grade === 'A') return null;
  const targetGrade = GRADE_ORDER[GRADE_ORDER.indexOf(grade) - 1];
  const target = GRADE_LOWER_BOUND[targetGrade];
  const lower = GRADE_LOWER_BOUND[grade];
  const pointsNeeded = Math.max(1, Math.ceil(target - score));
  const progressPct = Math.max(0, Math.min(100, Math.round(((score - lower) / (target - lower)) * 100)));
  return { targetGrade, pointsNeeded, progressPct };
}

/** Measured outcomes of past optimization runs — NOT a promise, NOT an estimate.
 * Source: read-only SQL over feed_health_snapshots (first pre vs. latest post,
 * feeds with >= 50 products), measured 2026-07-23, n=35: median 81.2,
 * p25 59.4, p75 86.7. Re-measure before changing (see sprint design doc). */
const MEASURED_POST_SCORES = { median: 81, p25: 59, p75: 87, feeds: 35 } as const;

const GRADE_STYLES: Record<FeedCheckGrade, string> = {
  A: 'bg-success-light text-success-ink border-success/30',
  B: 'bg-success-light text-success-ink border-success/30',
  C: 'bg-warning-light text-warning-ink border-warning/30',
  D: 'bg-warning-light text-warning-ink border-warning/30',
  F: 'bg-destructive-light text-destructive-ink border-destructive/30',
};

export function FeedCheckHeader({
  snapshot,
  children,
  hasPost,
  compareScore,
  compareCount,
  baselineReset = false,
  feedTotal,
  checkedAt,
  staleReason,
  chapterTotal,
  compareSnapshot,
  onRecheck,
  recheckBusy = false,
}: FeedCheckHeaderProps) {
  const { t, i18n } = useTranslation('optimization');
  const language = i18n?.language;
  const roundedScore = Math.round(Number(snapshot.avg_score));
  const grade = gradeForScore(roundedScore);
  const breakdown = buildBreakdown(snapshot, compareSnapshot);
  const goal = nextGradeGoal(roundedScore);
  const basisCount = Math.round(Number(snapshot.product_count ?? 0));

  // Delta nur aus zwei echten Messungen. Fehlt eine, wird das benannt statt
  // weggelassen: Leere liest sich als "kein Fortschritt", nicht als
  // "ungemessen".
  const deltaPoints = compareScore != null
    ? roundedScore - Math.round(Number(compareScore))
    : null;
  const compareBasis = compareCount != null ? Math.round(Number(compareCount)) : null;

  /**
   * Das Delta mit Vorzeichen, und das Minus ist das TYPOGRAFISCHE (U+2212),
   * nicht der Bindestrich, den `String(-1)` liefert.
   *
   * Nicht Pedanterie: die Verlustzeilen der Kosten-Liste eine Karte weiter
   * unten tragen `feedCheck.breakdown.cost` mit genau diesem Zeichen. Standen
   * beide im selben Blickfeld, las sich das Delta als "-1" und der Verlust als
   * "−9,0" -- zwei Zeichen fuer dasselbe Vorzeichen auf einer Seite. Gefunden
   * in der Nachkontrolle auf golfimport (Note F, Delta minus eins).
   */
  const deltaLabel = deltaPoints === null
    ? null
    : deltaPoints > 0
      ? `+${deltaPoints}`
      : deltaPoints < 0
        ? `−${Math.abs(deltaPoints)}`
        : String(deltaPoints);

  // Die Luecke zwischen Feed-Bestand und gescorter Menge. Nur wenn gemessen,
  // positiv UND mindestens ein Prozent der gescorten Menge (siehe die
  // Begruendung an der Prop): ein Bestand unter der Snapshot-Menge (Produkte
  // seit der Messung geloescht) ist keine "ohne Score"-Aussage, sondern eine
  // andere Geschichte, und die erfindet die Karte nicht.
  const unscoredCount = feedTotal != null && feedTotal > basisCount ? feedTotal - basisCount : 0;
  const showUnscored = unscoredCount > 0 && basisCount > 0 && unscoredCount / basisCount >= 0.01;

  // Der Satz zum groessten Verlust. `weak` und `strong` kommen aus
  // `buildBreakdown` bereits absteigend nach Verlust, und `weak` traegt per
  // Konstruktion die drei teuersten -- die Verkettung ist damit global
  // sortiert, es wird nicht neu sortiert (eine zweite Sortierung waere eine
  // zweite Wahrheit ueber die Reihenfolge).
  const sensible = [...breakdown.weak, ...breakdown.strong].filter((r) => r.cost >= SENSIBLE_LOSS_POINTS);
  const lossSentence = sensible.length === 0
    ? t('feedCheck.verdictLoss.none')
    : sensible.length === 1
      ? t('feedCheck.verdictLoss.single', { name: t(`feedCheck.components.${sensible[0].key}`) })
      : t('feedCheck.verdictLoss.pair', {
          first: t(`feedCheck.components.${sensible[0].key}`),
          second: t(`feedCheck.components.${sensible[1].key}`),
          points: Math.round(sensible[0].cost + sensible[1].cost),
        });

  // Spec 2.3: der groesste Verlust ist nicht immer die Optimierung wert
  // anzurechnen -- kommt er aus dem Feed des Haendlers oder aus Pass 2 und
  // stand nie ueber der 70er-Schwelle, sagt der Satz nur die Haelfte. Der
  // Zusatz haengt NUR am ersten (teuersten) Eintrag, nicht am zweiten aus
  // `verdictLoss.pair`: der Satz benennt ohnehin nur EINE Ursache.
  const lossSentenceWithTouch = sensible.length > 0 && sensible[0].touch === 'unchanged_low'
    ? lossSentence + t('feedCheck.verdictLoss.unchangedLowSuffix')
    : lossSentence;

  return (
    <Card
      data-testid="score-card"
      style={{
        background: 'hsl(var(--primary) / 0.06)',
        border: '1px solid hsl(var(--primary) / 0.12)',
        boxShadow: 'var(--shadow-md)',
      }}
    >
      <CardContent className="pt-6 pb-6 space-y-4">
        {/* Kapitel-Zaehler: nur im Report. Am Bildschirm tragen die Kapitel 2
            und 3 ihren Namen in ihrer eigenen Zeile (`FeedCheckChapters`), und
            ein "Kapitel 1 von 3" ueber der Score-Karte war die Ankuendigung
            einer Gliederung, die die Seite nicht mehr hat. */}
        {chapterTotal && (
          <span className="hidden print:block text-[11px] font-bold tracking-[0.08em] text-primary uppercase">
            {t('feedCheck.chapterLabel', { total: chapterTotal })}
          </span>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
          {/* Die Note steht IM Ring, nicht als eigene Skala daneben: die Skala
              A B C D F war die zweite Fassung derselben Zahl und nebenbei die
              groesste Flaeche der Karte.

              Der Ring ist zugleich der Trigger fuer das, was frueher als zwei
              eigene Zeilen darunter stand: der Micro-Goal-Satz ("Nur noch 6
              Punkte bis Note D") und der Methodik-Link.
              Warum ein Popover und nicht der Radix-Tooltip, den die
              Baustellen-Liste benutzt: der Tooltip schliesst, sobald der Zeiger
              von seinem Trigger auf seinen Inhalt wandert, ein Link darin ist
              mit der Maus also unerreichbar. Und warum nicht HoverCard, das
              genau fuer Vorschau-Inhalte gebaut ist: Radix erklaert es
              ausdruecklich als nicht tastaturbedienbar und nicht fuer
              wesentliche Inhalte. Bleibt das Popover -- Klick, Fokusfalle,
              Escape, und ein Link, der funktioniert. */}
          <Popover>
            <PopoverTrigger
              data-testid="grade-info-trigger"
              // S4: das aria-label ERSETZT den Inhalt fuer die Namensberechnung,
              // die Zahl im Ring faellt damit aus der Ansage -- und sie steht am
              // Bildschirm nirgends sonst (der Untertitel nennt Feed, Menge und
              // Pruefdatum, das Verdikt ist ein Satz ohne Zahl, die Notenskala
              // ist print-only). Also traegt das Label beides.
              aria-label={t('feedCheck.grade.ringLabel', { score: roundedScore, grade })}
              className="inline-flex w-fit shrink-0 self-start rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <HeroDonut score={roundedScore} size={132} grade={t('feedCheck.grade.ring', { grade })} />
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 space-y-2 text-xs">
              <p>
                {goal
                  ? t('feedCheck.grade.tooltipGoal', { count: goal.pointsNeeded, grade: goal.targetGrade })
                  : t('feedCheck.grade.tooltipTop')}
              </p>
              {/* D10: der Unscored-Hinweis steht HIER und nicht in der
                  Meta-Zeile. Gemessen sprengt er dort das Wortbudget: die Zeile
                  darf zwoelf Woerter tragen (Spec 4.3), und
                  Vergleichsbasis-Hinweis plus Unscored-Hinweis plus
                  Veraltungs-Chip sind allein in DE sechzehn. Inhaltlich gehoert
                  er ohnehin hierher -- er beantwortet "worueber redet diese
                  Zahl", also genau die Frage, die dieses Popover traegt. */}
              {showUnscored && (
                <p data-testid="score-basis-unscored" className="text-muted-foreground">
                  {t('feedCheck.meta.unscored', {
                    count: unscoredCount,
                    formatted: formatInteger(unscoredCount, language),
                  })}
                </p>
              )}
              <a
                href={t('feedCheck.methodologyUrl')}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block font-medium text-primary underline underline-offset-2"
              >
                {t('feedCheck.grade.tooltipMethod')}
              </a>
            </PopoverContent>
          </Popover>

          <div className="min-w-0 space-y-1.5">
            <h2 className="text-xl sm:text-2xl font-heading font-bold leading-snug">
              {t(`feedCheck.verdict.${grade}`)}
            </h2>
            <p data-testid="verdict-loss" className="text-sm text-muted-foreground">
              {lossSentenceWithTouch}
            </p>

            {/* Meta-Zeile, hoechstens zwoelf Woerter (Spec 4.3). Was hier
                frueher zusaetzlich stand -- Bezugsgroesse, Pruefdatum,
                Vorher-Basis -- traegt jetzt die Seitenkopfzeile
                beziehungsweise der Druck-Block weiter unten. */}
            <p data-testid="score-meta" className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
              {deltaPoints !== null ? (
                <span data-testid="score-delta" className={`font-medium ${scoreDeltaInkClass(deltaPoints)}`}>
                  {/* A2-NEU-5: "0 seit der Vorher-Messung" stand ohne Vorzeichen
                      und ohne Einheit da und las sich wie ein Fehler. */}
                  {deltaPoints === 0
                    ? t('feedCheck.meta.deltaZero')
                    : t('feedCheck.meta.delta', { delta: deltaLabel })}
                </span>
              ) : (
                // baselineReset: es gibt aeltere Befunde, nur keine mit der
                // heutigen Formel-Version -- der Grund ist der Formelwechsel,
                // nicht ein fehlendes "noch".
                <span>{t(baselineReset ? 'feedCheck.scoreBaselineReset' : 'feedCheck.scoreNoCompare')}</span>
              )}

              {staleReason && (
                // Warn-Paar aus den Projekt-Tokens, nicht rohes Tailwind-Amber:
                // `amber-700` auf dem Tint liegt unter AA. Das Paar
                // `bg-warning-light` / `text-warning-ink` ist dasselbe, das
                // `badge.tsx` und die Werkbank-Karte tragen; sein Kontrast ist
                // in src/lib/contrast.test.ts festgenagelt.
                <span
                  data-testid="stale-badge"
                  className="text-[11px] font-medium rounded px-1.5 py-0.5 bg-warning-light text-warning-ink"
                >
                  {t(`feedCheck.stale.${staleReason}`)}
                </span>
              )}

              {staleReason && onRecheck && (
                <button
                  type="button"
                  data-testid="stale-recheck"
                  onClick={onRecheck}
                  disabled={recheckBusy}
                  className="min-h-11 text-[11px] font-bold text-primary hover:underline disabled:opacity-50 print:hidden sm:min-h-0"
                >
                  {t('feedCheck.stale.recheck')}
                </button>
              )}
            </p>
          </div>
        </div>

        {/* ---------- ab hier nur Druck-Report ---------- */}

        {/* Die Notenskala. Sie beantwortet im Report die Frage, die am
            Bildschirm der Popover-Text beantwortet: wo liegt diese Note
            zwischen A und F. Ein gedrucktes Blatt hat kein Popover. */}
        <div
          className="hidden print:flex items-end gap-1.5"
          role="img"
          aria-label={t('feedCheck.gradeBandLabel', { grade })}
        >
          {GRADE_ORDER.map((g) => {
            const isActive = g === grade;
            return (
              <div
                key={g}
                data-testid={isActive ? 'grade-active' : undefined}
                className={
                  isActive
                    ? `flex h-16 w-16 items-center justify-center rounded-2xl border font-heading font-bold text-3xl ${GRADE_STYLES[g]}`
                    : 'flex h-9 w-9 items-center justify-center rounded-xl border border-transparent bg-muted/60 font-heading font-bold text-sm text-muted-foreground/50'
                }
              >
                {g}
              </div>
            );
          })}
        </div>

        {/* Bezugsgroesse, Vorher-Basis, Pruefdatum und Micro-Goal. Am
            Bildschirm stehen die ersten drei in der Seitenkopfzeile, das
            vierte im Popover. */}
        <div className="hidden print:block text-xs text-muted-foreground space-y-1">
          <p data-testid="score-basis">
            {t('feedCheck.scoreBasis', {
              count: basisCount,
              formatted: formatInteger(basisCount, language),
            })}
            {unscoredCount > 0 && (
              <>
                {' '}
                {t('feedCheck.scoreBasisUnscored', {
                  count: unscoredCount,
                  formatted: formatInteger(unscoredCount, language),
                })}
              </>
            )}
            {deltaPoints !== null && compareBasis !== null && (
              <>
                <span className="mx-1.5">·</span>
                {/* A2-NEU-5 auch im Druck: "0 Punkte gegenueber der
                    Vorher-Messung" las sich wie ein Fehler, am Bildschirm
                    steht dafuer `feedCheck.meta.deltaZero`. */}
                {deltaPoints === 0
                  ? t('feedCheck.scoreDeltaBasisZero', {
                      count: compareBasis,
                      formatted: formatInteger(compareBasis, language),
                    })
                  : t('feedCheck.scoreDeltaBasis', {
                      delta: deltaLabel,
                      count: compareBasis,
                      formatted: formatInteger(compareBasis, language),
                    })}
              </>
            )}
            {checkedAt && (
              <>
                <span className="mx-1.5">·</span>
                <span data-testid="checked-at">{t('feedCheck.checkedAt', { ago: timeAgo(checkedAt, t) })}</span>
              </>
            )}
          </p>
          <p>
            {goal
              ? t('feedCheck.grade.tooltipGoal', { count: goal.pointsNeeded, grade: goal.targetGrade })
              : t('feedCheck.grade.tooltipTop')}
          </p>
        </div>

        {/* Potenzial-Strip, nur im Erstkontakt UND nur im Report.
            Am Bildschirm war er die dritte Fassung derselben Zahl ("Dein Feed
            heute 34 von 100"), und Spec Abschnitt 1 nennt genau das als
            Messgroesse (Fassungen des Scores: 4 auf 1). Im Report, den ein
            Haendler ohne die App liest, ist der Vergleich mit dem gemessenen
            Median die Aussage, um die es geht. Ein Feed MIT Post-Snapshot
            wuerde sich hier mit seiner eigenen Gattung vergleichen und
            Potenzial behaupten, das er gerade gehoben hat. */}
        {!hasPost && roundedScore < 70 && (
          <div className="hidden print:block rounded-xl border border-primary/15 bg-card/60 p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
              <div>
                <span className="block text-[11px] font-bold tracking-[0.08em] text-muted-foreground uppercase">
                  {t('feedCheck.potential.todayLabel')}
                </span>
                <span className="text-xl font-heading font-bold text-foreground">
                  {t('feedCheck.potential.today', { score: roundedScore })}
                </span>
              </div>
              <div className="hidden sm:block h-8 w-px bg-border" />
              <div>
                <span className="block text-[11px] font-bold tracking-[0.08em] text-primary uppercase">
                  {t('feedCheck.potential.afterLabel')}
                </span>
                <span className="text-xl font-heading font-bold text-primary">
                  {t('feedCheck.potential.after', { median: MEASURED_POST_SCORES.median })}
                </span>
              </div>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {t('feedCheck.potential.basis', { p25: MEASURED_POST_SCORES.p25, p75: MEASURED_POST_SCORES.p75 })}
            </p>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {t('feedCheck.potential.basisNote', { feeds: MEASURED_POST_SCORES.feeds })}
            </p>
          </div>
        )}

        {children}
      </CardContent>
    </Card>
  );
}
